import React from "react";
import {
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
  ImageSourcePropType,
} from "react-native";
import Text from "@components/CustomText";
import ReturnDetail from "@assets/images/HomepageWidget/ReturnDetail.png";
import ChevronRight from "@assets/svg/ChevronRight";
import color from "@constants/color";
import analytics from "@services/analytics";
import { useNavigation } from "@react-navigation/native";
import { width } from "@constants/metrics";
import LoadingComponent from "./LoadingComponent";

interface ValueSection {
  title: string;
  value: number;
  amount: number;
}

interface WidgetItem {
  type: string;
  title: string;
  nav_to: string;
  nav_value?: number;
  nav_params?: Record<string, unknown>;
  subtitle?: string;
  value?: ValueSection[];
  subvalue?: string;
  caption_txt?: string;
  caption_value?: string;
  caption_color?: string;
  caption_icon?: "arrow";
  loading?: string[];
}

interface ExtraLargeWidgetProps {
  item: WidgetItem;
}

interface ImageMap {
  [key: string]: ImageSourcePropType;
}

const ExtraLargeWidget: React.FC<ExtraLargeWidgetProps> = React.memo(
  ({ item }) => {
    const navigation = useNavigation();

    const getImage = (type: string): ImageSourcePropType => {
      const imageMap: ImageMap = {
        "return-details": ReturnDetail,
      };
      return imageMap[type] || null;
    };

    const isloading = (key: string): boolean => {
      return item?.loading?.includes(key) || false;
    };

    const renderDetailsSection = (values: ValueSection[]) => (
      <View style={styles.detailsContainer}>
        <View style={styles.returnContent}>
          <View style={[styles.returnRow, { flexWrap: "wrap" }]}>
            {values.map((section, index) => (
              <View
                key={index}
                style={[styles.returnSection, { width: "45%" }]}
              >
                <Text variant="caption_2" style={styles.sectionTitle}>
                  {section.title}
                </Text>
                <LoadingComponent
                  visible={!isloading(section.title)}
                  style={{
                    maxWidth: "85%",
                  }}
                >
                  <View style={styles.valueRow}>
                    <Text variant="title_18" style={styles.returnQuantity}>
                      {section.value}
                    </Text>
                    <Text variant="caption_2" style={styles.qtyLabel}>
                      Qty.
                    </Text>
                    <View style={styles.amountContainer}>
                      <Text variant="caption_2" style={styles.amount}>
                        {`Amount: `}
                        <Text variant="caption_4" style={styles.amountText}>
                          {section.amount}
                        </Text>
                      </Text>
                    </View>
                  </View>
                </LoadingComponent>
              </View>
            ))}
          </View>
        </View>
      </View>
    );

    return (
      <TouchableOpacity
        testID="hpw-large-widget"
        style={[
          styles.container,
          Array.isArray(item?.value) && styles.returnContainer,
        ]}
        activeOpacity={0.8}
        disabled={item?.nav_to === ""}
        onPress={() => {
          if (item?.nav_to) {
            analytics.trackEvent(`${item?.type} Card Clicked`, {
              source: "Home Page Widget",
            });
            navigation.navigate(item.nav_to, {
              nav_params: item?.nav_params,
            });
          }
        }}
      >
        <View style={styles.header}>
          <Text variant="subtitle_16">{item?.title}</Text>
          <View style={styles.headerContent}>
            <View style={styles.chevronContainer}>
              <ChevronRight
                pathFill={color.lightMediumBlack}
                style={styles.chevronIcon}
              />
            </View>
          </View>
        </View>
        <View style={styles.divider} />
        {renderDetailsSection(item.value as ValueSection[])}
        <Image source={getImage(item?.type)} style={styles.image} />
      </TouchableOpacity>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    width: width - 32,
    backgroundColor: color.white,

    elevation: 2,
    marginHorizontal: 6,
    padding: 12,
    overflow: "hidden",
    marginVertical: 8,
  },
  returnContainer: {
    height: 180,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  headerContent: {
    flexDirection: "row",
  },
  chevronContainer: {
    backgroundColor: color.lightGrayWhite,
    alignSelf: "flex-start",
    padding: 3,
    borderRadius: 4,
  },
  chevronIcon: {
    height: 16,
    width: 16,
  },
  divider: {
    backgroundColor: color.shadowLighter,
    height: 2,
    width: "100%",
    marginVertical: 8,
  },

  image: {
    bottom: 0,
    right: 0,
    position: "absolute",
    height: 85,
    width: 115,
    resizeMode: "contain",
  },
  detailsContainer: {
    flex: 1,
  },
  returnContent: {
    flex: 1,
  },
  returnRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  returnSection: {
    marginRight: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    color: color.mediumBlacklightBlack,
  },
  valueRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  returnQuantity: {
    color: color.mediumBlack,
    marginRight: 2,
  },
  qtyLabel: {
    color: color.mediumBlacklightBlack,
    marginRight: 4,
  },
  amount: {
    color: color.mediumBlack,
  },
  amountText: {
    color: color.mediumBlack,
    fontWeight: "700",
  },
  amountContainer: {
    backgroundColor: color.lightGrey,
    padding: 2,
    paddingHorizontal: 6,
    borderRadius: 10,
  },
});

export default ExtraLargeWidget;
